import { useEffect, useState } from "react";
import { api, type DeletedTrip } from "./api.ts";
import { lastDay } from "./format.ts";
import { asLabel } from "./former.tsx";
import { ReportThis } from "./ReportThis.tsx";
import { navigate } from "./router.ts";
import { Button } from "./souvenir.ts";
import { Message } from "./ui.tsx";

// A deleted trip's page, as the people who were in it find it (its link, a page left open, its join
// link): who deleted it and when. Its owner can bring it back from here while they still can.
export function TripGone({ trip, you, onRestored }: { trip: DeletedTrip; you: string; onRestored?: () => void }) {
  const mine = trip.by.id === you;
  const restorable = mine && Date.parse(trip.restorable_until) > Date.now();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    document.title = `${trip.title} was deleted · Tripmo`;
  }, [trip.title]);
  const yourTrips = (
    <a
      className="tm-link body"
      href="/"
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        navigate("/");
      }}
    >
      Back to your trips
    </a>
  );
  return (
    <Message
      title={mine ? "You deleted this trip" : `${asLabel(trip.by.name)} deleted this trip`}
      body={`${trip.title} was deleted on ${lastDay(trip.at)}, for everyone in it.${
        restorable
          ? ` You can bring it back as it was, with everyone in it, until ${lastDay(trip.restorable_until)}.`
          : ""
      }`}
      action={
        <>
          {restorable && (
            <Button
              variant="primary"
              className="tm-edge-start"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  await api.restoreTrip(trip.id);
                  onRestored?.();
                } catch (e) {
                  setError(e instanceof Error ? e.message : "Couldn't restore it. Try again.");
                  setBusy(false);
                }
              }}
            >
              {busy ? "Restoring…" : "Restore this trip"}
            </Button>
          )}
          {error && (
            <p className="callout tm-error" role="alert">
              {error}
              <ReportThis />
            </p>
          )}
          {yourTrips}
        </>
      }
    />
  );
}
